import type {
  IAccountingFees,
  IAccountingFeesDetails,
} from "../interfaces/models/IAccountingFees";

export const DEFAULT_SPECIAL_FUND_NAME = "Special Fund";
export const DEFAULT_SPECIAL_FUND_PERCENTAGE = 10;

export type SpecialFundConfig = {
  specialFundName: string;
  specialFundPercentage: number;
};

export function resolveSpecialFundFromDoc(
  doc: IAccountingFees | null | undefined,
): SpecialFundConfig {
  const detail = doc?.details?.find((d) => d.type === "specialFund");
  if (!detail) {
    return {
      specialFundName: DEFAULT_SPECIAL_FUND_NAME,
      specialFundPercentage: DEFAULT_SPECIAL_FUND_PERCENTAGE,
    };
  }

  const name = String(detail.fundName ?? "").trim() || DEFAULT_SPECIAL_FUND_NAME;
  const rawPercent = Number(detail.fee);
  const specialFundPercentage = Number.isFinite(rawPercent)
    ? rawPercent
    : DEFAULT_SPECIAL_FUND_PERCENTAGE;

  return { specialFundName: name, specialFundPercentage };
}

export function calculateSpecialFundAmount(
  membershipBase: number,
  percentage: number,
): number {
  const base = Number(membershipBase);
  const pct = Number(percentage);
  if (!Number.isFinite(base) || !Number.isFinite(pct) || pct <= 0) {
    return 0;
  }
  return Math.round(base * (pct / 100) * 100) / 100;
}

export function formatSpecialFundInvoiceDescription(
  config: Pick<SpecialFundConfig, "specialFundName" | "specialFundPercentage">,
): string {
  const pct =
    config.specialFundPercentage % 1 === 0
      ? String(config.specialFundPercentage)
      : String(config.specialFundPercentage);
  return `${config.specialFundName} (${pct}% base)`;
}

export function buildSpecialFundDetail(
  fundName: string,
  percentage: number,
): IAccountingFeesDetails {
  const name = fundName.trim() || DEFAULT_SPECIAL_FUND_NAME;
  const pct = Number(percentage);
  return {
    type: "specialFund",
    fee: Number.isFinite(pct) ? pct : DEFAULT_SPECIAL_FUND_PERCENTAGE,
    fundName: name,
    increasePercentage: 0,
  };
}

export function upsertSpecialFundDetail(
  details: IAccountingFeesDetails[],
  fundName: string,
  percentage: number,
): IAccountingFeesDetails[] {
  const without = details.filter((d) => d.type !== "specialFund");
  return [...without, buildSpecialFundDetail(fundName, percentage)];
}

export function parseSpecialFundPercentageInput(value: string): number {
  const parsed = parseFloat(String(value ?? "").trim());
  return Number.isFinite(parsed) ? parsed : DEFAULT_SPECIAL_FUND_PERCENTAGE;
}
